// @flow
import React, { Component } from 'react';
import { Source, Layer } from 'react-mapbox-gl';

import MapContext from 'components/Map/MapContext';
import { Geofence } from 'utils/rt';

import diff from 'lodash/difference';

import type { FeatureCollection } from 'flow-geojson';

const RT_ENDPOINT_URI = '/api/v1/rt';
const FOLLOW_MARKER_RADIUS = 40;

const CANCEL_REASONS = {
  exitted: 'A követett légijármű elhagyta a megfigyelt légteret.',
  landed: 'A követett légijármű leszállt.',
};

export default class RTOverlay extends Component<*, FeatureCollection> {
  fetchTimer: IntervalID;

  handleConnect = () => this.props.setFeatures([]);

  handleDefineOrEnter = feature => {
    const { features, setFeatures } = this.props;
    const { id } = feature;

    if (features.find(f => f.id === id)) {
      return this.handleInside(feature);
    }

    // eslint-disable-next-line no-param-reassign
    feature.properties.url = `ac/${feature.id}`;

    setFeatures([...features, feature]);
  };

  handleInside = feature => {
    const { geometry, id, properties } = feature;
    const {
      features,
      setFeatures,
      setSelectedFeature,
      visibleAirspaces,
    } = this.props;

    const match = features.find(f => f.id === id);
    if (!match) {
      return;
    }

    match.geometry.coordinates = [...geometry.coordinates];
    [, , match.properties.altitude] = geometry.coordinates;
    match.properties.track = properties.track;
    const newFeature = { ...match };

    setFeatures(features.map(f => (f.id === id ? newFeature : f)));

    if (id === this.props.followedAircraft) {
      if (properties && properties.overlaps) {
        properties.overlaps = properties.overlaps.filter(
          ({ id, altitude_range: [lower, upper] }) => {
            const { altitude } = match.properties;
            return (
              visibleAirspaces.includes(id) &&
              altitude >= lower &&
              altitude <= upper
            );
          }
        );
        this.trackProximityEvents(newFeature.properties, properties);
        newFeature.properties = properties;
      }

      this.trackAircraftOnMap();
      setSelectedFeature(properties);
    }
  };

  handleExit = feature => {
    const { id } = feature;
    const { features, setFeatures } = this.props;

    setFeatures(features.filter(f => f.id !== id));

    if (id === this.props.followedAircraft) {
      this.cancelTracking(CANCEL_REASONS.exitted);
    }
  };

  cancelTracking = reason => {
    this.props.showErrorMessage(reason);
    this.props.setFollowedAircraft(null);
    this.props.setSelectedFeature(null);
  };

  trackProximityEvents = (oldProps, newProps) => {
    const { showErrorMessage } = this.props;
    const overlapEvent = this.computeProximityEvent('overlaps')(
      oldProps,
      newProps
    );

    if (overlapEvent) {
      const { event, ...props } = overlapEvent;

      if (event !== 'noop') {
        if (event === 'cross') {
          showErrorMessage(`Légtérváltás: ${props.from} → ${props.to}`, {
            playSound: true,
          });
        } else if (event === 'leave') {
          showErrorMessage(`${props.from} légtér elhagyása`, {
            playSound: true,
          });
        } else if (event === 'enter') {
          showErrorMessage(`Belépés a ${props.to} légtérbe`, {
            playSound: true,
          });
        } else {
          showErrorMessage('Az átlapoló légterek megváltoztak', {
            playSound: true,
          });
        }
      }
    }

    const nearbyEvent = this.computeProximityEvent('nearby_aircraft')(
      oldProps,
      newProps
    );

    if (nearbyEvent) {
      const { event, ...props } = nearbyEvent;

      if (event === 'enter') {
        showErrorMessage(`FIGYELEM: közeli légijármű!`, { playSound: true });
      }
    }
  };

  computeProximityEvent = key => (oldProps, newProps) => {
    if (!oldProps[key] || !newProps[key]) {
      return null;
    }

    const oldNames = oldProps[key].map(e => e.name);
    const newNames = newProps[key].map(e => e.name);

    const leftOuter = diff(oldNames, newNames);
    const rightOuter = diff(newNames, oldNames);

    if (leftOuter.length === 0 && rightOuter.length === 0) {
      return {
        event: 'noop',
      };
    } else if (leftOuter.length === 1 && rightOuter.length === 1) {
      return {
        event: 'cross',
        from: leftOuter[0],
        to: rightOuter[0],
      };
    } else if (leftOuter.length === 1 && rightOuter.length === 0) {
      return {
        event: 'leave',
        from: leftOuter[0],
      };
    } else if (leftOuter.length === 0 && rightOuter.length === 1) {
      return {
        event: 'enter',
        to: rightOuter[0],
      };
    }
    return {
      event: 'multiple',
    };
  };

  trackAircraftOnMap = justStarted => {
    const { features, followedAircraft, setViewport } = this.props;
    const match = features.find(f => f.id === followedAircraft);

    if (match) {
      const {
        geometry,
        properties: { track: bearing },
      } = match;

      setViewport({
        center: [...geometry.coordinates.slice(0, 2)],
        bearing: [bearing],

        pitch: [50],
        zoom: [justStarted ? 10 : this.map.getZoom()],

        fitBounds: null,
        flyToOptions: {
          offset: [0, -150],
        },
      });

      this.forceUpdate();
    }
  };

  componentDidUpdate({ followedAircraft }) {
    if (followedAircraft !== this.props.followedAircraft) {
      if (this.map) {
        // On tracking end: reset pitch/bearing
        if (this.props.followedAircraft === null) {
          this.map.flyTo({
            bearing: 0,
            pitch: 0,
          });
        }
      }

      this.trackAircraftOnMap(true);
      this.geofence.send({ follow: this.props.followedAircraft });
    }
  }

  componentDidMount() {
    this.geofence = new Geofence(RT_ENDPOINT_URI, {
      onConnect: this.handleConnect,
      onDefine: this.handleDefineOrEnter,
      onEnter: this.handleDefineOrEnter,
      onExit: this.handleExit,
      onInside: this.handleInside,
    });
  }

  componentWillUnmount() {
    this.geofence.close();
  }

  render() {
    const {
      selectedFeature,
      followedAircraft,
      features,
      appInDrawMode,
    } = this.props;

    if (appInDrawMode) {
      return null;
    }

    return (
      <MapContext.Consumer>
        {map => {
          if (!this.map && map) {
            this.map = map;

            map.on(
              'click',
              'rt-cluster-circles',
              ({
                features: [
                  {
                    geometry: { coordinates: center },
                  },
                ],
              }) => {
                map.flyTo({ center, zoom: map.getZoom() + 2 });
              }
            );
          }

          return [
            <Source
              key="rt-source"
              id="rt-source"
              geoJsonSource={{
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features,
                },
                cluster: true,
                clusterRadius: 20,
                clusterMaxZoom: 10,
              }}
            />,
            <Source
              key="rt-trace-source"
              id="rt-trace"
              geoJsonSource={{
                type: 'geojson',
                data: selectedFeature
                  ? selectedFeature.trace
                  : {
                      type: 'FeatureCollection',
                      features: [],
                    },
              }}
            />,
            <Layer
              key="rt-trace-layer"
              id="rt-trace"
              sourceId="rt-trace"
              type="line"
            />,
            <Layer
              key="rt-follow-marker-shadow-layer"
              id="rt-follow-marker-shadow"
              sourceId="rt-source"
              type="circle"
              filter={[
                'all',
                ['==', ['get', 'id'], followedAircraft],
                ['!=', ['get', 'id'], null],
                ['!', ['has', 'point_count']],
              ]}
              paint={{
                'circle-color': 'transparent',
                'circle-radius': FOLLOW_MARKER_RADIUS,
                'circle-stroke-width': 4,
                'circle-stroke-color': 'black',
                'circle-blur': 3,
              }}
            />,
            <Layer
              key="rt-follow-marker-layer"
              id="rt-follow-marker"
              sourceId="rt-source"
              type="circle"
              filter={[
                'all',
                ['==', ['get', 'id'], followedAircraft],
                ['!=', ['get', 'id'], null],
                ['!', ['has', 'point_count']],
              ]}
              paint={{
                'circle-color': 'rgba(0, 0, 0, .15)',
                'circle-radius': FOLLOW_MARKER_RADIUS,
                'circle-stroke-width': 4,
                'circle-stroke-color': 'white',
              }}
            />,
            <Layer
              key="rt-follow-marker-accent-layer"
              id="rt-follow-marker-accent"
              sourceId="rt-source"
              type="circle"
              filter={[
                'all',
                ['==', ['get', 'id'], followedAircraft],
                ['!=', ['get', 'id'], null],
                ['!', ['has', 'point_count']],
              ]}
              paint={{
                'circle-color': 'transparent',
                'circle-radius': FOLLOW_MARKER_RADIUS + 2,
                'circle-stroke-width': 2,
                'circle-stroke-color': 'black',
              }}
            />,
            <Layer
              key="rt-layer"
              id="aircrafts"
              sourceId="rt-source"
              type="symbol"
              filter={[
                'all',
                ['!', ['has', 'point_count']],
                ['!', ['==', ['get', 'aircraft_type'], 'unknown']],
              ]}
              layout={{
                'icon-image': ['get', 'aircraft_type'],
                'icon-rotation-alignment': 'map',
                // 'text-rotation-alignment': 'map',
                'icon-rotate': [
                  'case',
                  ['==', ['get', 'aircraft_type'], 'ground-station'],
                  0,
                  ['to-number', ['coalesce', ['get', 'track'], 0]],
                ],

                'icon-allow-overlap': true,
                'text-allow-overlap': true,

                'text-field': [
                  'coalesce',
                  ['get', 'name'],
                  ['get', 'address'],
                  ['get', 'id'],
                ],
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Regular'],
                'text-offset': [0, 2.25],
                'text-size': 11,
                'text-rotation-alignment': 'viewport',
              }}
              paint={{
                'text-halo-color': 'white',
                'text-halo-width': 2,
                'text-halo-blur': 1,
              }}
            />,
            <Layer
              key="rt-cluster-circles"
              id="rt-cluster-circles"
              sourceId="rt-source"
              filter={['has', 'point_count']}
              type="circle"
              paint={{
                'circle-color': 'black',
                'circle-radius': 10,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': 'white',
              }}
            />,
            <Layer
              key="rt-cluster-labels-layer"
              id="rt-cluster-labels"
              sourceId="rt-source"
              filter={['has', 'point_count']}
              type="symbol"
              layout={{
                'text-allow-overlap': true,
                'text-field': ['to-string', ['get', 'point_count']],
                'text-size': 13,
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Regular'],
                'text-rotation-alignment': 'viewport',
              }}
              paint={{
                'text-color': 'white',
                'text-halo-color': 'black',
                'text-halo-width': 2,
              }}
            />,
          ];
        }}
      </MapContext.Consumer>
    );
  }
}



// WEBPACK FOOTER //
// ./overlays/RTOverlay/RTOverlay.js